import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { auth } from '../lib/api';
import { useTenantBranding } from '../contexts/TenantBrandingContext';
import InstallAppCard from '../components/InstallAppCard';
import IOSInstallHint from '../components/IOSInstallHint';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const branding = useTenantBranding();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await auth.login(username, password);
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Giriş başarılı!');
      // BM and employees go to quick access, GM goes to branches
      if (user.role === 'GENEL_MUDUR') {
        navigate('/branches');
      } else {
        navigate('/home');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Boot marker — confirms the login screen rendered without crashing.
  try {
    if (typeof window !== 'undefined' && (window as any).__bs_report) {
      (window as any).__bs_report({ kind: 'LOGIN_RENDERED' });
    }
  } catch (_) {}

  return (
    <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            {branding.loading ? (
              <div className="h-14 mb-3" />
            ) : branding.logo_url ? (
              <img src={`${BACKEND}${branding.logo_url}`} alt={branding.display_name || ''} className="h-14 mx-auto mb-3 object-contain" />
            ) : (
              <h1 className="text-3xl font-bold text-[#6b0f1a] mb-2" data-testid="login-brand-name">{branding.display_name || 'Stüdyo'}</h1>
            )}
            <p className="text-gray-600">Sube Yonetim Sistemi</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kullanıcı Adı
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a] focus:border-transparent"
                required
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Şifre
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a] focus:border-transparent"
                required
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6b0f1a] text-white py-3 rounded-lg font-semibold hover:bg-[#8a1422] disabled:opacity-50 transition-colors"
              data-testid="login-button"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <a href="/forgot-password" className="text-sm text-[#6b0f1a] hover:underline font-medium" data-testid="staff-forgot-password-link">
              Şifremi Unuttum
            </a>
          </div>

          <div className="mt-6 text-center">
            <a href="/member-login" className="text-sm text-gray-600 hover:text-[#6b0f1a]">
              Üye Girişi →
            </a>
          </div>
        </div>

        {/* Install-to-home-screen helper: renders Android install button when
            `beforeinstallprompt` fires and always offers expandable iPhone/Android steps. */}
        <InstallAppCard className="mt-4" />
      </div>
      {/* iOS Safari: auto-showing bottom-sheet with share-icon hint (once per device). */}
      <IOSInstallHint />
    </div>
  );
};

export default Login;